import { Badge, type BadgeProps, Group } from '@mantine/core'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { LuAnchor, LuShip } from 'react-icons/lu'
import { type MarinaStatusKind, statusColorMap } from './colors'

interface Props extends Omit<BadgeProps, 'color'> {
  status: string
  kind: MarinaStatusKind
  /** Legacy uppercase rendering — most callers should leave this off. */
  upper?: boolean
}

const ICONS: Record<string, ReactNode> = {
  CHECKED_IN: <LuAnchor size={11} />,
  CHECKED_OUT: <LuShip size={11} />,
}

export function StatusBadge({ status, kind, upper = false, variant = 'light', size = 'sm', ...rest }: Props) {
  const { t } = useTranslation()
  const color = statusColorMap[kind]?.[status] ?? 'gray'
  const icon = kind === 'booking' ? ICONS[status] : undefined

  const translated = t(`status.${kind}.${status}`, { defaultValue: '' })
  const fallback = status
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ')
  const label = upper ? (translated || fallback).toUpperCase() : translated || fallback

  return (
    <Badge
      color={color}
      variant={variant}
      size={size}
      radius="sm"
      styles={{ label: { whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' } }}
      leftSection={
        icon ? (
          <Group gap={0} align="center">
            {icon}
          </Group>
        ) : undefined
      }
      {...rest}
    >
      {label}
    </Badge>
  )
}
